import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FileModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { PatchFileParameters } from '../../../../../../../typings/api/skymap/rest/v1/file';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useFocusElement } from '../../../../hooks/use-focus-element';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { TextBox } from '../../../text-box/text-box';

interface Props {
  file: FileModel;
  onClose: (canceled: boolean) => void;
}

enum Status {
  Idle,
  Saving,
  Error,
}

const RenameFileDialog = (props: Props) => {
  const { t } = useTranslation();
  const ref = useFocusElement<HTMLInputElement>(
    React.useMemo(() => {
      const index = props.file.name.lastIndexOf('.');
      return index > -1 ? { start: 0, end: index } : true;
    }, [props.file.name]),
  );

  const { handleError, getErrorForPath, clearErrorForPath } =
    useErrorHandling<RequestParamsToApiPaths<PatchFileParameters>>();
  const [status, setStatus] = React.useState(Status.Idle);
  const [name, setName] = React.useState<string>(props.file.name);

  const saveButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      const fileService = SkyMapAxiosServiceFactory.instance.createFileServiceV1();
      await fileService.patchFile({
        path: { fileId: props.file.id },
        body: { name: name },
      });

      props.onClose(false);
    } catch (err) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  const cancelButtonClick = () => {
    props.onClose(true);
  };

  const isButtonDisabled = () => {
    if (status === Status.Saving) {
      return true;
    }

    return name === props.file.name;
  };

  return (
    <Dialog
      closeIcon={status !== Status.Saving}
      closeOnDimmerClick={true}
      width={350}
      onClose={() => props.onClose(true)}
    >
      {{
        header: t('fileManager.renameFileDialog.title', { ns: 'components' }),
        content: (
          <>
            <Label>
              <span>{t('fileManager.renameFileDialog.nameField', { ns: 'components' })}</span>
              <TextBox
                disabled={status === Status.Saving}
                errorMessage={getErrorForPath('body.name')}
                placeholder={props.file.name}
                ref={ref}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearErrorForPath('body.name');
                }}
              />
            </Label>
          </>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isButtonDisabled()}
                loading={status === Status.Saving}
                variant="contained"
                onClick={saveButtonClick}
              >
                {t('save', { ns: 'common' })}
              </Button>
              <Button
                disabled={status === Status.Saving}
                variant="text"
                onClick={cancelButtonClick}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Label = styled.label`
  span {
    display: block;
    margin-bottom: 0.4em;
    font-weight: 300;
    font-size: 0.9em;
  }
`;

export { RenameFileDialog };
